import { useEffect, useState } from 'react';
import { Empty, Switch, Table, TableProps, TabsProps } from 'antd';

// services
import NotificationService from 'core/services/notification';

// components
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import TableCustomCell from 'components/tableCustomCell/tableCustomCell';
import AuthActionComponent from 'components/authAction/authAction';

// models
import { onGetPointSubscriptions } from 'modules/subscription/services/subscriptions';
import { PointSubscription, PointWalletSubscription } from 'modules/subscription/models/subscriptions';
import { Link } from 'react-router-dom';

// styles
import styles from './list.module.scss';
import NoData from 'components/noData/noData';
import { useTranslation } from 'react-i18next';

function ListSubscriptionsPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [pointWalletSubscriptions, setPointWalletSubscriptions] = useState<PointWalletSubscription>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<PointSubscription>['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
			render: (id) => <span style={{ fontWeight: 500, color: '#3F4254' }}>{id}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.USER'),
			dataIndex: 'user',
			key: 'user',
			render: (id, pointSubscription) => (
				<Link to={`/users/view/${pointSubscription.payable_id}`} style={{ textDecoration: 'none' }}>
					<TableCustomCell title={pointSubscription.payable_name} />
				</Link>
			),
		},
		{
			title: t('SUBSCRIPTIONS.TYPE'),
			dataIndex: 'type',
			key: 'type',
			render: (type) => (
				<span className="status" style={{ fontWeight: 500, color: '#7E8299' }}>
					{type}
				</span>
			),
		},
		{
			title: t('SUBSCRIPTIONS.DESCRIPTION'),
			dataIndex: 'description',
			key: 'description',
			render: (description) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{description}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.POINTS'),
			dataIndex: 'points',
			key: 'points',
			render: (points) => <span>{points}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.BASE_AMOUNT'),
			dataIndex: 'base_amount',
			key: 'base_amount',
			render: (base_amount) => <span>{base_amount}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.BASE_AMOUNT'),
			dataIndex: 'vat_amount',
			key: 'vat_amount',
			render: (vat_amount) => <span>{vat_amount}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.BASE_AMOUNT'),
			dataIndex: 'total_amount',
			key: 'total_amount',
			render: (total_amount) => <span>{total_amount}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.CURRENCY'),
			dataIndex: 'currency',
			key: 'currency',
			render: (currency) => <span>{t(`COMMON.${currency}`)}</span>,
		},
		{
			title: t('SUBSCRIPTIONS.DATE'),
			dataIndex: 'date',
			key: 'date',
			render: (date) => <span style={{ fontWeight: 500, color: '#7E8299' }}>{date}</span>,
		},
	];

	useEffect(() => {
		getSubscriptions();
	}, []);

	const getSubscriptions = async () => {
		try {
			setIsLoading(true);

			const response = await onGetPointSubscriptions();
			setPointWalletSubscriptions(response.data.data);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
			setIsLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetPointSubscriptions(page);
			setPointWalletSubscriptions(response.data.data);

			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('SUBSCRIPTIONS.TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<div className={`sectionContainer listData`}>
						<header>
							<div>
								<h5>
									{totalRecords} {t('SUBSCRIPTIONS.COUNT')}
								</h5>
								<p>{t('SUBSCRIPTIONS.DESCRIPTION')}</p>
							</div>
						</header>

						<div className={styles.data_details}>
							<div className="px-5">
								<span>{t('SUBSCRIPTIONS.BALANCE')}</span>
								<p>{pointWalletSubscriptions?.balanceExcludingVat}</p>
							</div>
							<div className="px-5">
								<span>{t('SUBSCRIPTIONS.VAT')}</span>
								<p>{pointWalletSubscriptions?.totalVat}</p>
							</div>
							<div className="px-5">
								<span>{t('SUBSCRIPTIONS.TOTAL')}</span>
								<p>{pointWalletSubscriptions?.balanceIncludingVat}</p>
							</div>
						</div>

						<div className="position-relative">
							<Table
								rowKey="id"
								columns={columns}
								dataSource={pointWalletSubscriptions?.point_wallet_transactions}
								scroll={{ x: 400 }}
								pagination={{
									pageSize: 15,
									total: totalRecords,
									showSizeChanger: false,
									current: currentPage,
									onChange: fetchMoreData,
								}}
								locale={{ emptyText: <NoData /> }}
							/>
							{isLoadMore && (
								<div className="loadMoreContainer">
									<LoadingComponent />
								</div>
							)}
						</div>
					</div>
				</>
			)}
		</>
	);
}

export default ListSubscriptionsPage;
